import { DataObject, InfoOutlined } from '@mui/icons-material';
import { Stack, Tooltip, Switch, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MergeTag } from 'domains/content/types';
import TextField from 'domains/core/components/TextField';
import InputLabel from 'domains/core/components/InputLabel';
import DropdownButton from 'domains/core/components/DropdownButton';
import { DropdownButtonOption } from 'domains/core/components/DropdownButton/DropdownButton';
import useContactColumns from 'hooks/queries/useContactColumns';
import { themeColors } from 'colors';
import { getFriendlyName, getMergeTags, getMessageWithMergeTags } from 'utils';

type Props = {
    handleMessageChange?: (updatedMessage: string) => void;
    isMessageLoading: boolean;
    label?: string;
    message?: string;
    rows?: number;
    width?: string;
};

const MessageInputWithMergeTags = ({
    handleMessageChange,
    isMessageLoading,
    label = '',
    message = '',
    rows,
    width,
}: Props) => {
    const [cursorPosition, setCursorPosition] = useState(0);
    const inputRef = useRef(null);
    const [contentForPreview, setContentForPreview] = useState('');
    const [previewEnabled, setPreviewEnabled] = useState(false);
    const [isContentPreviewed, setIsContentPreviewed] = useState(false);

    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();

    const mergeTags = useMemo(() => {
        if (isContactColumnsSuccess) return getMergeTags(contactColumns);
    }, [contactColumns, isContactColumnsSuccess]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.selectionStart = cursorPosition;
            inputRef.current.selectionEnd = cursorPosition;
        }
    }, [cursorPosition]);

    const resetContentPreview = () => {
        setIsContentPreviewed(false);
    };

    const handleContentFieldChange = (e: any) => {
        if (isContentPreviewed) {
            resetContentPreview();
        } else {
            setCursorPosition(e.target.selectionStart);
            handleMessageChange(e.target.value);
        }
    };

    const onClickMergeTagItem = (value?: string) => {
        if (isContentPreviewed) resetContentPreview();
        inputRef.current.focus();

        const addMergeTagToContent = () => {
            if (cursorPosition === 0) return value + message.slice(cursorPosition);

            if (cursorPosition) {
                return message.slice(0, cursorPosition) + value + message.slice(cursorPosition);
            } else {
                return message.slice(0, cursorPosition) + value;
            }
        };

        const newContent = addMergeTagToContent();
        handleMessageChange(newContent);
    };

    const orderedMergeTags = mergeTags?.sort((a, b) => a.value.localeCompare(b.value));
    const mergeTagOptions: DropdownButtonOption[] = orderedMergeTags?.map(({ name, value }: MergeTag) => ({
        label: <InputLabel label={getFriendlyName(name)} />,
        onClick: (): void => onClickMergeTagItem(value),
    }));

    const handlePreviewClick = () => {
        setIsContentPreviewed(!isContentPreviewed);

        const previewContent = isContactColumnsSuccess ? getMessageWithMergeTags(mergeTags, message) : '';
        setContentForPreview(previewContent);
    };

    useEffect(() => {
        const regex = /{{.*?}}/g;
        const matches = message.match(regex);
        if (matches) {
            setPreviewEnabled(true);
        } else {
            setPreviewEnabled(false);
        }
    }, [message]);

    return (
        <Stack rowGap={1} maxWidth="800px">
            <TextField
                counterCurrent={message?.length}
                counterMax={1600}
                disabled={isMessageLoading}
                inputProps={{ maxLength: 1600, 'data-testid': 'content-field' }}
                inputRef={inputRef}
                label={label}
                multiline
                onBlur={(e) => setCursorPosition(e.target.selectionStart)}
                onChange={handleContentFieldChange}
                placeholder="Enter your message here — this will be converted to speech"
                rows={rows ?? 9}
                value={isContentPreviewed ? contentForPreview : message}
                width={width ?? '100%'}
            />
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row">
                    <DropdownButton
                        disabled={isMessageLoading}
                        data-testid="add-merge-tag"
                        variant="text"
                        label="Merge Tags"
                        sx={{ height: 'auto', marginRight: 2, padding: '4px 8px' }}
                        icon={null}
                        startIcon={<DataObject sx={{ marginLeft: 0.5, marginRight: -0.25 }} />}
                        options={isContactColumnsSuccess ? mergeTagOptions : []}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                color="secondary"
                                checked={isContentPreviewed}
                                disabled={!previewEnabled}
                                onChange={handlePreviewClick}
                                size="small"
                            />
                        }
                        label={
                            <Typography
                                color={previewEnabled ? `${themeColors.text.primary}` : `${themeColors.text.disabled}`}
                                variant="body2"
                            >
                                Preview Merge Tags
                            </Typography>
                        }
                    />
                </Stack>
                <Tooltip
                    aria-label="Keep the message under 400 characters for better engagement."
                    title="Keep the message under 400 characters for better engagement."
                >
                    <Stack direction="row" alignItems="flex-start">
                        <Typography fontSize="14px">{message?.length}</Typography>
                        <InfoOutlined sx={{ fontSize: '14px', margin: '3px 0 0 4px' }} />
                    </Stack>
                </Tooltip>
            </Stack>
        </Stack>
    );
};

export default MessageInputWithMergeTags;
